import { attachToolReview } from '/js/toolReviewWidget.js';
import { matchesPromoPlatform } from '/js/platform.js';

const PROMO_SELECTOR = '[data-promo-card]';

class PromoCardWidget {
  constructor(root) {
    this.root = root;
    this.currentPromoIndex = 0;
    this.interval = null;
    this.availablePromos = [];
    this.touchStartX = 0;
    this.touchEndX = 0;
    this.isTransitioning = false;
    this.carouselContainer = null;
    this.dotsContainer = null;
    this.prevBtn = null;
    this.nextBtn = null;
    this.stableHeight = 0;
  }

  init() {
    this.cacheElements();
    this.availablePromos = this.extractTemplates();
    if (!this.availablePromos.length) {
      return;
    }

    this.applyStableHeightFromDom();
    this.renderCurrent();
    this.carouselContainer.classList.remove('promo-card-nojs');
    this.setupControls();
    this.setupEventListeners();
    this.startAutoplay();
  }

  cacheElements() {
    this.carouselContainer = this.root.querySelector('.promo-card-container');
    this.dotsContainer = this.root.querySelector('.promo-card-dots');
    this.prevBtn = this.root.querySelector('.promo-card-nav-prev');
    this.nextBtn = this.root.querySelector('.promo-card-nav-next');
  }

  extractTemplates() {
    if (!this.carouselContainer) {
      return [];
    }

    // Le card per un'altra piattaforma (nchova sui Mac, Switcho altrove) escono dal
    // DOM prima di misurare l'altezza stabile e di contare i pallini.
    this.carouselContainer.querySelectorAll('.promo-card[data-promo-for]').forEach((card) => {
      if (!matchesPromoPlatform(card)) card.remove();
    });

    const cards = Array.from(this.carouselContainer.querySelectorAll('.promo-card'));
    const reviewCard = cards.find(
      (card) => card.classList.contains('promo-card-review') || card.dataset.promoSlide === 'review'
    );
    const promoCards = cards.filter((card) => card !== reviewCard);

    const templates = [];

    if (reviewCard?.dataset.toolSlug?.trim()) {
      templates.push({
        type: 'review',
        html: reviewCard.outerHTML,
      });
    }

    templates.push(
      ...promoCards.map((card) => ({
        html: card.outerHTML,
        partner: card.dataset.partner || null,
      }))
    );

    return templates;
  }

  getCurrentTemplate() {
    return this.availablePromos[this.currentPromoIndex] || this.availablePromos[0];
  }

  renderCurrent() {
    if (!this.carouselContainer) {
      return;
    }

    this.carouselContainer.innerHTML = '';
    const card = this.createPromoCardFromTemplate(this.getCurrentTemplate());
    if (card) {
      this.carouselContainer.appendChild(card);
      const cardHeight = card.offsetHeight;
      if (cardHeight > 0) {
        this.carouselContainer.style.minHeight = `${Math.max(this.stableHeight, cardHeight)}px`;
      }
    }
  }

  applyStableHeightFromDom() {
    if (!this.carouselContainer) {
      return;
    }

    const cards = Array.from(this.carouselContainer.querySelectorAll('.promo-card'));
    if (!cards.length) {
      return;
    }

    let maxHeight = 0;
    cards.forEach((card) => {
      const prevDisplay = card.style.display;
      const prevPosition = card.style.position;
      const prevVisibility = card.style.visibility;
      const prevLeft = card.style.left;
      const prevTop = card.style.top;
      const prevWidth = card.style.width;

      card.style.display = 'flex';
      card.style.position = 'absolute';
      card.style.visibility = 'hidden';
      card.style.left = '0';
      card.style.top = '0';
      card.style.width = '100%';

      maxHeight = Math.max(maxHeight, card.offsetHeight);

      card.style.display = prevDisplay;
      card.style.position = prevPosition;
      card.style.visibility = prevVisibility;
      card.style.left = prevLeft;
      card.style.top = prevTop;
      card.style.width = prevWidth;
    });

    if (maxHeight > 0) {
      this.stableHeight = maxHeight;
      this.carouselContainer.style.minHeight = `${maxHeight}px`;
    }
  }

  createPromoCardFromTemplate(template) {
    if (!template) {
      return null;
    }

    if (template.type === 'review') {
      const wrapper = document.createElement('div');
      wrapper.innerHTML = template.html;
      const card = wrapper.firstElementChild;
      if (!card) {
        return null;
      }
      card.hidden = false;
      card.removeAttribute('hidden');
      attachToolReview(card);
      return card;
    }

    const wrapper = document.createElement('div');
    wrapper.innerHTML = template.html;
    const card = wrapper.firstElementChild;

    if (!card) {
      return null;
    }

    if (card.tagName === 'A') {
      const linkOptions = card.dataset.linkOptions ? JSON.parse(card.dataset.linkOptions) : null;
      if (Array.isArray(linkOptions) && linkOptions.length > 1) {
        const randomIndex = Math.floor(Math.random() * linkOptions.length);
        card.href = linkOptions[randomIndex];
      }
      // Click tracking handled by trackedLinks.js via data-track attribute
    }

    return card;
  }

  setupControls() {
    const controls = this.root.querySelector('.promo-card-controls');
    const showControls = this.availablePromos.length > 1;

    if (!controls) {
      return;
    }

    controls.style.display = showControls ? 'flex' : 'none';

    if (!showControls || !this.dotsContainer) {
      return;
    }

    this.dotsContainer.innerHTML = this.availablePromos
      .map((_, index) => {
        const activeClass = index === this.currentPromoIndex ? 'active' : '';
        return `
          <button class="promo-card-dot ${activeClass}" aria-label="Go to slide ${index + 1}"></button>
        `;
      })
      .join('');

    this.setupDotsHandlers();
  }

  setupDotsHandlers() {
    if (!this.dotsContainer) {
      return;
    }

    this.dotsContainer.querySelectorAll('.promo-card-dot').forEach((dot, index) => {
      dot.addEventListener('click', () => this.goToPromo(index));
    });
  }

  setupEventListeners() {
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.prevPromo());
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.nextPromo());
    }

    const carousel = this.root.querySelector('.promo-card-carousel');
    if (carousel) {
      carousel.addEventListener('touchstart', (event) => {
        this.touchStartX = event.changedTouches[0].screenX;
      });

      carousel.addEventListener('touchend', (event) => {
        this.touchEndX = event.changedTouches[0].screenX;
        this.handleSwipe();
      });

      carousel.addEventListener('mouseenter', () => this.stopAutoplay());
      carousel.addEventListener('mouseleave', () => this.startAutoplay());
    }
  }

  updateDots() {
    if (!this.dotsContainer) {
      return;
    }

    this.dotsContainer.querySelectorAll('.promo-card-dot').forEach((dot, index) => {
      dot.classList.toggle('active', index === this.currentPromoIndex);
    });
  }

  startAutoplay() {
    const rotationEnabled = this.root.getAttribute('data-rotation-enabled') !== 'false';

    if (!rotationEnabled || this.availablePromos.length <= 1) {
      return;
    }

    this.stopAutoplay();
    this.interval = setInterval(() => {
      this.nextPromo();
    }, 15000);
  }

  stopAutoplay() {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
  }

  nextPromo() {
    if (this.availablePromos.length <= 1 || this.isTransitioning) return;

    this.isTransitioning = true;
    this.currentPromoIndex = (this.currentPromoIndex + 1) % this.availablePromos.length;
    this.animateTransition('next');
  }

  prevPromo() {
    if (this.availablePromos.length <= 1 || this.isTransitioning) return;

    this.isTransitioning = true;
    this.currentPromoIndex =
      (this.currentPromoIndex - 1 + this.availablePromos.length) % this.availablePromos.length;
    this.animateTransition('prev');
  }

  goToPromo(index) {
    if (index === this.currentPromoIndex || this.isTransitioning) return;

    this.isTransitioning = true;
    const direction = index > this.currentPromoIndex ? 'next' : 'prev';
    this.currentPromoIndex = index;
    this.animateTransition(direction);
  }

  animateTransition(direction) {
    if (!this.carouselContainer) {
      this.isTransitioning = false;
      return;
    }

    const oldCard = this.carouselContainer.querySelector('.promo-card');
    const newCard = this.createPromoCardFromTemplate(this.getCurrentTemplate());

    if (!newCard) {
      this.isTransitioning = false;
      return;
    }

    const currentHeight = oldCard ? oldCard.offsetHeight : 0;
    const slideOffset = 106;
    newCard.style.position = 'absolute';
    newCard.style.width = '100%';
    newCard.style.transform =
      direction === 'next' ? `translateX(${slideOffset}%)` : `translateX(-${slideOffset}%)`;

    if (oldCard) {
      oldCard.style.position = 'absolute';
      oldCard.style.width = '100%';
    }

    this.carouselContainer.appendChild(newCard);
    const nextHeight = newCard.offsetHeight;
    const minHeight = Math.max(this.stableHeight, currentHeight, nextHeight);
    if (minHeight > 0) {
      this.carouselContainer.style.minHeight = `${minHeight}px`;
    }
    newCard.offsetHeight;

    requestAnimationFrame(() => {
      if (oldCard) {
        oldCard.style.transform =
          direction === 'next' ? `translateX(-${slideOffset}%)` : `translateX(${slideOffset}%)`;
      }
      newCard.style.transform = 'translateX(0)';

      setTimeout(() => {
        if (oldCard) {
          oldCard.remove();
        }
        newCard.style.position = 'relative';
        this.isTransitioning = false;
        this.updateDots();
      }, 300);
    });

    this.startAutoplay();
  }

  handleSwipe() {
    const swipeThreshold = 50;
    const diff = this.touchStartX - this.touchEndX;

    if (Math.abs(diff) > swipeThreshold) {
      if (diff > 0) {
        this.nextPromo();
      } else {
        this.prevPromo();
      }
    }
  }
}

function isPromoInHiddenResultSection(root) {
  const section = root.closest('.result-section');
  return Boolean(section && getComputedStyle(section).display === 'none');
}

export function initPromoCards(scope = document) {
  const roots = scope === document
    ? [...document.querySelectorAll(PROMO_SELECTOR)]
    : scope.matches?.(PROMO_SELECTOR)
      ? [scope]
      : [...scope.querySelectorAll(PROMO_SELECTOR)];

  roots.forEach((root) => {
    if (isPromoInHiddenResultSection(root)) {
      return;
    }

    if (root.dataset.promoCardReady === 'true') {
      return;
    }

    root.dataset.promoCardReady = 'true';
    const widget = new PromoCardWidget(root);
    root._promoCardWidget = widget;
    widget.init();
  });
}

function observeHiddenResultSections() {
  document.querySelectorAll('.result-section').forEach((section) => {
    if (getComputedStyle(section).display === 'none') {
      const observer = new MutationObserver(() => {
        if (getComputedStyle(section).display !== 'none') {
          initPromoCards(section);
          observer.disconnect();
        }
      });
      observer.observe(section, { attributes: true, attributeFilter: ['style', 'class'] });
    }
  });
}

const scheduleInitPromoCards = () => {
  const run = () => {
    initPromoCards();
    observeHiddenResultSections();
  };

  if (document.readyState === 'complete') {
    run();
    return;
  }

  document.addEventListener('DOMContentLoaded', run, { once: true });
};

scheduleInitPromoCards();

if (typeof window !== 'undefined') {
  window.initPromoCards = initPromoCards;
}
